// src/contexts/SessionsDataContext.tsx
import { logger } from '../utils/logger';
// PERFORMANCE OPTIMIZATION: Preloads all sessions data in the background
// This makes switching to the Sessions tab instant instead of showing loading spinners

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { sessionService } from '../services/sessionService';
import { profileService } from '../services/profileService';
import { SessionWithDetails } from '../types/session';
import { Profile } from '../types/profile';
import { isProfileComplete } from '../utils/profileUtils';

interface SessionsDataContextType {
  // Data
  sessions: SessionWithDetails[];
  profile: Profile | null;
  profileComplete: boolean;
  currentUserId: string;

  // Loading state
  loading: boolean;

  // Actions
  refresh: () => Promise<void>;
}

const SessionsDataContext = createContext<SessionsDataContextType | undefined>(undefined);

export const useSessionsData = () => {
  const context = useContext(SessionsDataContext);
  if (!context) {
    throw new Error('useSessionsData must be used within SessionsDataProvider');
  }
  return context;
};

export const SessionsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<SessionWithDetails[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Load sessions
  const loadSessions = useCallback(async () => {
    try {
      const data = await sessionService.getMySessions();
      setSessions(data);
    } catch (error) {
      logger.error('Error loading sessions:', error);
    }
  }, []);

  // Load profile
  const loadProfile = useCallback(async () => {
    try {
      const profileData = await profileService.getCurrentProfile();
      setProfile(profileData);
      const complete = isProfileComplete(profileData);
      setProfileComplete(complete);
    } catch (error) {
      logger.error('Error loading profile:', error);
    }
  }, []);

  // Refresh all data
  const refresh = useCallback(async () => {
    await Promise.all([
      loadSessions(),
      loadProfile(),
    ]);
  }, [loadSessions, loadProfile]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setCurrentUserId(user.id);

      // Load profile and sessions in parallel
      await Promise.all([
        loadProfile(),
        loadSessions(),
      ]);

      setLoading(false);
      setInitialized(true);
    };

    init();
  }, [loadProfile, loadSessions]);

  // Subscribe to real-time session updates
  useEffect(() => {
    if (!initialized || !currentUserId) return;

    // Subscribe to sessions table changes
    const sessionsSubscription = supabase
      .channel('sessions_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
        },
        () => {
          loadSessions();
        }
      )
      .subscribe();

    // Subscribe to session_participants changes (for RSVP updates)
    const participantsSubscription = supabase
      .channel('session_participants_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_participants',
          filter: `user_id=eq.${currentUserId}`
        },
        () => {
          loadSessions();
        }
      )
      .subscribe();

    return () => {
      sessionsSubscription.unsubscribe();
      participantsSubscription.unsubscribe();
    };
  }, [initialized, currentUserId, loadSessions]);

  const value: SessionsDataContextType = {
    sessions,
    profile,
    profileComplete,
    currentUserId,
    loading,
    refresh,
  };

  return (
    <SessionsDataContext.Provider value={value}>
      {children}
    </SessionsDataContext.Provider>
  );
};